import {useContext, useEffect, useState} from 'react';

import md5 from 'md5';

import {useHistoryData} from 'app/openai/utils';
import {ChatTypes} from 'app/subscriptions/hooks';
import {chatSubscriptionRegistry} from 'app/subscriptions/subscriptionRegistry';
import {gt} from 'app/utils/i18n';
import {reportToSentry} from 'app/utils/sentry';
import {WebsocketContext} from 'app/ws/context';

import type {CommandType, StreamedAICommandType} from './types';
import type {ChatMessageType} from 'app/openai/components/TextEdition/ChatMessage';
import type {WsChatMessage} from 'app/subscriptions/hooks';

export type WebsocketChatProps = {
  object_id?: number;
  content_type?: string;
  type?: ChatTypes;
  thread_id: string;
};

export function useWsOpenaiMessage({
  object_id,
  content_type,
  type,
  thread_id,
}: WebsocketChatProps) {
  const {sendWebsocketMessage, isWSClosing, isWSOpen} =
    useContext(WebsocketContext);

  const [rawResponseData, setRawResponseData] = useState<WsChatMessage | null>(
    null
  );

  const [isLoading, setIsLoading] = useState<boolean | null>(null);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [message, setMessage] = useState('');

  const [lastCommandsHash, setLastCommandsHash] = useState('');
  const [commands, setCommands] = useState<Array<StreamedAICommandType>>([]);

  const updateErrorState = (newError: string | null) => {
    if (newError == null) {
      setError(null);
      setIsError(false);
    } else {
      setError(newError);
      setIsError(true);
    }
  };

  useEffect(() => {
    if (isWSClosing) {
      updateErrorState(
        gt(
          "I'm sorry the connection with SweetAI has closed unexpectedly. Please try again later."
        )
      );
    } else {
      updateErrorState(null);
    }
  }, [isWSClosing]);

  const updateCommandsIfChanged = (
    newCommands: Array<StreamedAICommandType>
  ) => {
    const newCommandHash = md5(JSON.stringify(newCommands));
    if (newCommandHash !== lastCommandsHash) {
      setCommands(newCommands);
      setLastCommandsHash(newCommandHash);
    }
  };

  const sendMessage = (
    message: string,
    message_object_id?: number,
    message_content_type?: string,
    message_type?: string
  ) => {
    if (isWSOpen) {
      setIsLoading(true);
      const data: any = {
        message,
        object_id: object_id || message_object_id,
        content_type: content_type || message_content_type,
        thread_id,
        type: type || message_type,
      };

      sendWebsocketMessage(
        JSON.stringify({
          event: 'chat.update',
          data,
        })
      );
    } else {
      updateErrorState(
        gt(
          "I'm sorry the connection with SweetAI has closed unexpectedly. Please try again later."
        )
      );
    }
  };

  function mapActionsWithType(
    actions: Array<Omit<StreamedAICommandType, 'command'>> | undefined,
    command: CommandType
  ): Array<StreamedAICommandType> {
    return (actions || []).map(
      (action: Omit<StreamedAICommandType, 'command'>) => ({
        ...action,
        command,
      })
    ) as Array<StreamedAICommandType>;
  }

  const processStreamedWSResponse = (data: WsChatMessage) => {
    try {
      const flattenedCommands = [
        ...mapActionsWithType(data.message.actions, 'actions'),
        ...mapActionsWithType(data.message.step_actions, 'step_actions'),
        ...mapActionsWithType(data.message.add_actions, 'add_actions'),
        ...mapActionsWithType(data.message.remove_actions, 'remove_actions'),
      ];
      if (flattenedCommands) {
        updateCommandsIfChanged(flattenedCommands);
      }
      setMessage(data.message.message ?? '');
    } catch (error) {
      updateErrorState(
        gt(
          'Unable to process SweetAI response. Try repeating your last chat message'
        )
      );
      reportToSentry(error, undefined, false);
    }
  };

  const receiveChatMessage = (data) => {
    if (!isLoading) {
      setIsLoading(true);
    }
    if (data.type === 'error') {
      updateErrorState(data.message);
      setIsLoading(false);
    } else if (
      data.type === 'edit_policy_chat' ||
      data.type === 'edit_procedure_chat' ||
      data.type === 'document_suggestions' ||
      data.type === 'rag' ||
      data.type === 'ragaccount'
    ) {
      updateErrorState(null);
      processStreamedWSResponse(data);
    } else if (data.type === 'edit_text_chat') {
      setMessage(data.message ?? '');
      updateErrorState(null);
      setIsLoading(false);
    } else if (data.type === 'end_stream') {
      updateCommandsIfChanged([{stop: true, command: 'stop'}]);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (rawResponseData != null) {
      receiveChatMessage(rawResponseData);
    }
  }, [rawResponseData]);

  useEffect(() => {
    if (window.WebSocket) {
      const fillResponseData = (data: WsChatMessage) => {
        if (data.thread_id === thread_id) {
          setRawResponseData(data);
        }
      };
      chatSubscriptionRegistry.register(fillResponseData);
      return () => chatSubscriptionRegistry.unregister(fillResponseData);
    }
  }, [thread_id]);

  return {
    sendMessage,
    isLoading,
    isError,
    error,
    message,
    commands,
    isOpen: isWSOpen,
  };
}

export function useSendMessageWithHistory(
  wsProps: WebsocketChatProps,
  chatMessage: string,
  setChatMessage: (message: string) => void,
  initialAIMessage?: string
) {
  const [messageHistory, appendMessageHistory, setCurrentMessage] =
    useHistoryData<ChatMessageType>();
  const [chatStatus, setChatStatus] = useState<
    'ready' | 'sending' | 'processing' | 'error' | 'streaming'
  >('ready');

  useEffect(() => {
    if (initialAIMessage != null) {
      appendMessageHistory({
        source: 'ai',
        text: initialAIMessage,
      });
    }
  }, []);

  const submitMessage = () => {
    sendMessage(chatMessage);
    setChatStatus('sending');
    appendMessageHistory({source: 'me', text: chatMessage});
  };

  const {sendMessage, message, isLoading, isError, error} =
    useWsOpenaiMessage(wsProps);

  useEffect(() => {
    if (
      (chatStatus === 'sending' ||
        chatStatus === 'processing' ||
        chatStatus === 'streaming') &&
      error
    ) {
      appendMessageHistory({
        source: 'error',
        text: error,
      });
      setChatStatus('error');
    }
  }, [isError]);

  useEffect(() => {
    if (isLoading) {
      setChatStatus('processing');
    } else {
      setChatStatus('ready');
    }
  }, [isLoading]);

  useEffect(() => {
    if (message) {
      setCurrentMessage({
        source: 'ai',
        text: message,
      });
      setChatMessage('');
      setChatStatus('streaming');
    }
  }, [message]);

  return {messageHistory, chatStatus, submitMessage};
}
